import { Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Core";
import { FeedClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import FormattedAvailability from "components/FormattedAvailability";
import IconTrophy from "components/Icons/IconTrophy";
import IconWonCounter from "components/Icons/IconWonCounter";
import MuiFakeButton from "components/MuiExtras/Button/MuiFakeButton";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiImage from "components/MuiExtras/MuiImage";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { BadgeAdminListDto, DashboardAwardAnalysisDetailColumn } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import UserListDrawer from "views/Dashboards/components/UserListDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuShareAnchorEl: any;
    menuAnchorEl: any;
    mouseOver: boolean;
    dialog: any;
}

interface IProps {
    badge: BadgeAdminListDto;
    onEdit?: () => void;
    onDelete?: () => void;
}

type Props = IProps & WithTranslation & WithSnackbar & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class BadgeCard extends React.PureComponent<Props, IState> {

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            mouseOver: false,
            menuAnchorEl: null,
            menuShareAnchorEl: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Gamification.Components.BadgeCard." + r, f);
    }

    ///
    private badgeDrillDown(badge: BadgeAdminListDto) {
        this.setState({
            dialog: (
                <UserListDrawer
                    title={this.props.t("Views.Dashboards.AwardAnalysis.Graph.UserListTitle")}
                    subtitle={badge.name}
                    showTotalColumn={true}
                    onGetData={
                        (searchtext, offset) =>
                            api.getDashboardAwardAnalysis({
                                detailColumn: DashboardAwardAnalysisDetailColumn.BadgesDrillDown,
                                startDate: null,
                                endDate: null,
                                memberId: null,
                                drillDownFilter: {
                                    id: badge.badgeId,
                                    title: searchtext,
                                },
                                range: { offset: offset, take: 20 },
                            })
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private handleEdit = () => {
        this.handleCloseMenu();
        this.props.onEdit();
    };

    ///
    private handleDelete = () => {
        this.handleCloseMenu();
        this.props.onDelete();
    };

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null, mouseOver: false });
    }

    ///
    private buildOverflowMenu(): Array<any> {

        const actionMenu: Array<any> = [];

        if (this.props.onEdit) actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{this.props.t("Common.MenuAction.Edit")}</MenuItem>);

        if (this.props.onDelete) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div1" />);
            actionMenu.push(<MenuItem key="delete" onClick={this.handleDelete}><MuiColor variant="error">{this.props.t("Common.MenuAction.Delete")}</MuiColor></MenuItem>);
        }
        return actionMenu;
    }

    ///
    public render() {
        const {
            classes,
            badge
        } = this.props;

        const { mouseOver } = this.state;

        const overflowActions = this.buildOverflowMenu();

        const badgName = badge.isActive
            ? <b>{badge.name}</b>
            : <MuiWarning><b>{badge.name}</b></MuiWarning>;

        return (
            <>
                <MuiPaper className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <Grid container spacing={2}>
                        <Grid item>
                            <MuiImage size="sm" orientation="square" src={badge.thumbnailUrl} />
                        </Grid>
                        <Grid item className={GlobalClassKeys.grow}>

                            <Grid container direction="row" justifyContent="space-between">
                                <Grid item>
                                    <Typography variant="h6">
                                        {
                                            this.props.onEdit
                                                ? <MuiClickable onClick={this.props.onEdit}>{badgName}</MuiClickable>
                                                : badgName
                                        }
                                    </Typography>
                                    {
                                        (badge.availableStartDate || badge.availableEndDate) &&
                                        <Typography variant="subtitle1">
                                            <MuiColor variant={badge.expired ? "warning" : "muted"}>
                                                <small>
                                                    <FormattedAvailability startDate={badge.availableStartDate} endDate={badge.availableEndDate} />
                                                </small>
                                            </MuiColor>
                                        </Typography>
                                    }
                                </Grid>
                                <Grid item>
                                    <IconButtonEdit
                                        size="small"
                                        visible={mouseOver}
                                        onClick={this.handleEdit}
                                    />
                                </Grid>
                            </Grid>

                            <Grid item container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                                className={this.props.classes.cardActions}
                            >
                                <Grid item>
                                    <MuiFakeButton
                                        color="transparent"
                                        title={this.res("ButtonPointsTitle")}
                                    >
                                        <IconTrophy /><span>{badge.points}</span>
                                    </MuiFakeButton>

                                    <MuiRegularButton
                                        color="transparent"
                                        title={this.res("ButtonWonPointsTitle")}
                                        onClick={() => this.badgeDrillDown(badge)}
                                    >
                                        <IconWonCounter /><span>{badge.wonCount}</span>
                                    </MuiRegularButton>
                                </Grid>

                                <Grid item>
                                    <ButtonEllipsis
                                        onClick={
                                            (e) => {
                                                this.setState({ menuAnchorEl: e.currentTarget });
                                            }
                                        }
                                    />
                                    <Menu
                                        open={this.state.menuAnchorEl != null}
                                        anchorEl={this.state.menuAnchorEl}
                                        onClose={() => { this.handleCloseMenu(); }}
                                    >
                                        {overflowActions}
                                    </Menu>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                </MuiPaper>

                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(BadgeCard));